import { useState, useEffect } from 'react';

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface ClientManagementRecord {
  id: number;
  name: string;
  filename?: string;
  /** Region / country code from CRM when present (e.g. UK, US). Omitted in older summaries. */
  country?: string;
  originalCurrency: string;
  totalInvoiced: number;            // USD
  totalInvoicedBaseCurrency: number;
  totalPaid?: number;
  outstanding: number;              // USD
  paymentRatio: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  deposit: number;
  depositAdjustment: number;
  netDeposit: number;
  modified?: string;
}

/** Per-client metrics stored inside period JSON files */
export interface PeriodClientMetrics {
  totalInvoiced: number;
  totalInvoicedBaseCurrency: number;
  originalCurrency: string;
  outstanding: number;
  totalPaid: number;
  paymentRatio: number;
  riskLevel: 'Low' | 'Medium' | 'High';
}

export interface PeriodBucket {
  total_usd?: number;
  total_hours?: number;
  clients: Record<string, number>;      // clientId → USD invoice amount
  clients_hours?: Record<string, number>;
}

export interface PeriodData {
  metadata?: unknown;
  by_year: Record<string, PeriodBucket>;
  by_year_month: Record<string, PeriodBucket>;
  client_names?: Record<string, string>;
  client_tax_pct?: Record<string, number>;
}

export interface SummaryPeriodBucket {
  clients: Record<string, PeriodClientMetrics>;
}

export interface PeriodByPeriod {
  metadata?: unknown;
  by_year: Record<string, SummaryPeriodBucket>;
  by_year_month: Record<string, SummaryPeriodBucket>;
  client_names?: Record<string, string>;
}

export interface ClientSummaryJson {
  metadata?: unknown;
  clients: ClientManagementRecord[];
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */

interface UseClientManagementDataResult {
  clients: ClientManagementRecord[];
  periodData: PeriodData | null;
  summaryByPeriod: PeriodByPeriod | null;
  loading: boolean;
  error: string | null;
}

export function useClientManagementData(): UseClientManagementDataResult {
  const [clients, setClients] = useState<ClientManagementRecord[]>([]);
  const [periodData, setPeriodData] = useState<PeriodData | null>(null);
  const [summaryByPeriod, setSummaryByPeriod] = useState<PeriodByPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [summaryRes, invoiceRes, metricRes] = await Promise.all([
          fetch('/clients-summary.json'),
          fetch('/client_invoice_by_period.json'),
          fetch('/clients_summary_by_period.json'),
        ]);

        if (!summaryRes.ok) throw new Error(`clients-summary.json: ${summaryRes.status}`);
        if (!invoiceRes.ok) throw new Error(`client_invoice_by_period.json: ${invoiceRes.status}`);
        if (!metricRes.ok) throw new Error(`clients_summary_by_period.json: ${metricRes.status}`);

        const [summaryJson, invoiceJson, metricJson]: [
          ClientSummaryJson,
          PeriodData,
          PeriodByPeriod
        ] = await Promise.all([summaryRes.json(), invoiceRes.json(), metricRes.json()]);

        if (cancelled) return;

        setClients(summaryJson.clients ?? []);
        setPeriodData(invoiceJson);
        setSummaryByPeriod(metricJson);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unknown error loading data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  return { clients, periodData, summaryByPeriod, loading, error };
}
